import { TableCellWithBadge } from "../../../../data-model/tableCells";
import {
  isNycbsDeniedClientOutcomeLabel,
  isNycbsDeniedResponseStatus,
} from "./nycbsClientOutcome";

const DEFAULT_TEXT_COLOR = "#000000";
const DEFAULT_BG_COLOR = "#F5F5F5";
const EMPTY_VALUE = "--";

/** Display label for denied orders with no backend outcome classification. */
export const NYCBS_OUTCOME_CLASS_OTHERS = "Others";

const EMPTY_CLASSIFICATION_VALUES = new Set([
  "",
  "--",
  "null",
  "none",
  "nan",
  "undefined",
]);

/**
 * Returns true when the backend outcome classification is unset or a placeholder.
 *
 * @param paOutcomeClassification - Raw `pa_outcome_classification` from the API
 * @return boolean: True when no meaningful classification is present
 */
export const isEmptyPaOutcomeClassification = (
  paOutcomeClassification: unknown,
): boolean => {
  if (paOutcomeClassification == null) {
    return true;
  }

  if (typeof paOutcomeClassification !== "string") {
    return true;
  }

  const normalized = paOutcomeClassification.trim().toLowerCase();
  return EMPTY_CLASSIFICATION_VALUES.has(normalized);
};

/** Inputs used to resolve the Outcome Class badge for a NYCBS External row. */
export interface NycbsOutcomeClassBadgeInput {
  /** Client-facing Outcome label already shown in the Outcome column. */
  clientOutcomeLabel: string;
  /** Raw `response_status` from the order row. */
  responseStatus: string | null | undefined;
  /** Raw `pa_outcome_classification` from the API. */
  paOutcomeClassification: unknown;
}

/**
 * Resolves the Outcome Class label shown in the NYCBS External table.
 *
 * When `pa_outcome_classification` is set, that value is shown. When it is empty:
 * - Denied outcomes require a denied `response_status` before showing "Others".
 * - All other outcomes show "Others" immediately.
 *
 * @param input - Outcome label, response status, and classification from the row
 * @return string: Outcome Class label for the table badge
 */
export const resolveNycbsOutcomeClassDisplayText = ({
  clientOutcomeLabel,
  responseStatus,
  paOutcomeClassification,
}: NycbsOutcomeClassBadgeInput): string => {
  if (!isEmptyPaOutcomeClassification(paOutcomeClassification)) {
    return String(paOutcomeClassification).trim();
  }

  if (isNycbsDeniedClientOutcomeLabel(clientOutcomeLabel)) {
    return isNycbsDeniedResponseStatus(responseStatus)
      ? NYCBS_OUTCOME_CLASS_OTHERS
      : EMPTY_VALUE;
  }

  return NYCBS_OUTCOME_CLASS_OTHERS;
};

/**
 * Builds the Outcome Class badge from outcome label, response status, and classification.
 *
 * @param input - Outcome label, response status, and classification from the row
 * @return TableCellWithBadge: Badge showing the resolved Outcome Class label
 */
export const getNycbsOutcomeClassBadge = (
  input: NycbsOutcomeClassBadgeInput,
): TableCellWithBadge => {
  const text = resolveNycbsOutcomeClassDisplayText(input);

  return {
    text,
    color: DEFAULT_TEXT_COLOR,
    bgColor: DEFAULT_BG_COLOR,
    hoverText: text,
  };
};
